import type { ResponseWithData, RouteParams} from '../helpers/ajax';
import {getJson, postJson, resolveRoute, responseToError} from '../helpers/ajax'
import type {JsonRequest, JsonResponse, JsonResponseObject} from '../helpers/jsonSerializable'
import {useEffect, useState} from 'react'
import {useMergeExistingReducer} from './useMergeReducer'
import useDebouncedDeepCompareEffect, {useDeepCompareMemoize} from './useDebouncedDeepCompareEffect'
import usePromise from './usePromise'
import {useMountEffect} from './useMountEffect'


export default function useGetServerData<ResData extends JsonResponseObject, StateData extends ExtendsObject = ResData>(route: RouteParams, defaults: StateData, transform?: (data: ResData) => StateData | void) {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)
    const [data, mergeData] = useMergeExistingReducer<StateData>(defaults)

    const url = resolveRoute(route)

    useEffect(() => {
        setLoading(true)
        getJson<ResData>(url).then(res => {
            if(res.ok) {
                mergeData(transform?.(res.data) || res.data || {})
                setError(null)
            } else {
                setError(responseToError(res))
            }
        }, err => {
            setError(err)
        }).finally(() => {
            setLoading(false)
        })
    }, [url])

    return {loading, data, error}
}

interface PostServerDataOptions<TReq, TRes> {
    initialData?: TRes
    delay?: number
}

export function usePostServerDataDebounced<TReq extends JsonRequest, TRes extends JsonResponse>(route: RouteParams, requestData: TReq, options: PostServerDataOptions<TReq,TRes>={}) {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)
    const [data, setData] = useState<TRes|null>(options.initialData ?? null)
    const url = resolveRoute(route)

    useDebouncedDeepCompareEffect(() => {
        setLoading(true)
        postJson<TRes,TReq>(url, requestData).then(res => {
            if(res.ok) {
                setData(res.data)
                setError(null)
            } else {
                setError(responseToError(res))
            }
        }, err => {
            setError(err)
        }).finally(() => {
            setLoading(false)
        })
    }, [url, requestData], options.delay ?? 333)

    return {loading, data, error} as {loading:true,data:TRes|null,error:null}|{loading:false,data:TRes,error:null}|{loading:false,data:TRes|null,error:Error}
}

export function usePostServerData<TReq extends JsonRequest, TRes extends JsonResponse>(url: string, requestData: TReq) {
    requestData = useDeepCompareMemoize(requestData)

    return usePromise(() => postJson<TRes,TReq>(url, requestData).then(res => {
        if(res.ok) {
            return res.data
        }
        throw responseToError(res)
    }), [url,requestData])
}

export function usePostServerDataOnce<TReq extends JsonRequest, TRes extends JsonResponse>(url: string, requestData: TReq, callback: (data: ResponseWithData<TRes>) => void) {
    const [loading, setLoading] = useState(true)

    useMountEffect(() => {
        postJson<TRes, TReq>(url, requestData).then(callback).finally(() => {
            setLoading(false)
        })
    })

    return loading
}
